import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { CheckCircle, TrendingDown, Clock, Package, ArrowRight, DollarSign } from 'lucide-react';

const ForeignTradeZones = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Foreign Trade Zone<br />Services
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8">
              Unlock significant cost savings and competitive advantages through strategic FTZ utilization. Expert guidance from application through activation and ongoing operations.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-slate-700 hover:bg-slate-800 text-white px-10 h-14">
                Explore FTZ Benefits
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Enormous Opportunities to Compete Globally
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl">
              Foreign Trade Zones present a formidable arsenal for businesses engaged in international trade, offering unique opportunities to mitigate costs and enhance supply chain efficiency. Operating within an FTZ provides substantial financial and operational advantages.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-slate-700 mb-2">Duty Deferral</h3>
              <p className="text-gray-600 text-sm">Pay duties only when goods enter U.S. commerce</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingDown className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-slate-700 mb-2">Duty Reduction</h3>
              <p className="text-gray-600 text-sm">Inverted tariff scenarios and manufacturing benefits</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-slate-700 mb-2">Duty Elimination</h3>
              <p className="text-gray-600 text-sm">No duty on re-exported goods or waste/scrap</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-slate-700" />
              </div>
              <h3 className="text-2xl font-bold text-slate-700 mb-2">Cash Flow</h3>
              <p className="text-gray-600 text-sm">Improved working capital through deferred payments</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Benefits of FTZ Operations</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Defer customs duties until goods enter U.S. commerce</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Eliminate duties on exported finished goods</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Reduce duties through inverted tariff scenarios</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Streamlined customs compliance and entry process</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Flexibility in inventory management and storage</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">No duty on waste, scrap, or obsolete materials</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Who Benefits Most from FTZs?</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Manufacturers assembling imported components</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Distribution centers handling international goods</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Companies with significant re-export activity</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Businesses facing inverted tariff situations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Operations requiring extended inventory holding</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-slate-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Companies seeking improved cash flow management</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12">
            Complete FTZ Services
          </h2>

          <div className="space-y-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Feasibility Analysis & Cost-Benefit Assessment</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Comprehensive evaluation of your business operations to quantify potential FTZ benefits. Detailed financial modeling of duty savings, cash flow improvements, and operational efficiencies.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Analysis of import/export patterns and volumes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>HTS classification review for duty savings opportunities</span>
                  </li>
                </ul>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Financial modeling of FTZ benefits vs. costs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Inverted tariff scenario identification</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">FTZ Application & Activation</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Complete preparation and submission of FTZ applications to CBP and the Foreign-Trade Zones Board. Management of the entire approval process from application through activation.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Application preparation and submission</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Coordination with CBP Port Directors</span>
                  </li>
                </ul>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Site surveys and documentation preparation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Activation procedures and compliance setup</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">FTZ Operations & Compliance Management</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Ongoing support for FTZ operations including inventory control procedures, admission/entry processing, and regulatory compliance. Training and documentation to ensure smooth operations.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Weekly entry procedures and documentation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Inventory control and recordkeeping systems</span>
                  </li>
                </ul>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Operator and staff training programs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-slate-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>CBP compliance audits and examinations support</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Discover Your FTZ Savings Potential
          </h2>
          <p className="text-base sm:text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Let us evaluate how Foreign Trade Zone utilization can enhance your competitive position and bottom line.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Request FTZ Feasibility Analysis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForeignTradeZones;